import { useState, useEffect, useRef } from 'react';
import { Question } from '../types/game';
import { getQuestionId } from '../utils/gameUtils';
import { FLIP_DELAY, FADE_OUT_DURATION } from '../constants/gameConstants';

interface UseQuestionFlipProps {
  currentQuestion: Question | null;
  liveAnswers: Array<{ playerId: string }>;
  totalPlayers: number;
  answerSubmitted: boolean;
  hostIsPlaying: boolean;
  onQuestionChange?: (question: Question | null) => void;
}

interface UseQuestionFlipReturn {
  showQuestionFlip: boolean;
  setShowQuestionFlip: React.Dispatch<React.SetStateAction<boolean>>;
  isFadingOut: boolean;
  previousQuestionIdForFlipRef: React.MutableRefObject<string | undefined>;
}

/**
 * Hook to manage question flip animation (flip to "Did you know?" when all players answer or timer runs out)
 */
export const useQuestionFlip = ({
  currentQuestion,
  liveAnswers,
  totalPlayers,
  answerSubmitted,
  hostIsPlaying,
  onQuestionChange,
}: UseQuestionFlipProps): UseQuestionFlipReturn => {
  const [showQuestionFlip, setShowQuestionFlip] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousQuestionIdForFlipRef = useRef<string | undefined>(undefined);

  // Reset flip when question changes (backup check)
  useEffect(() => {
    if (currentQuestion) {
      const questionId = getQuestionId(currentQuestion);
      if (questionId !== previousQuestionIdForFlipRef.current) {
        // If "did you know" is showing, fade it out
        if (showQuestionFlip) {
          setIsFadingOut(true);
          if (fadeOutTimeoutRef.current) {
            clearTimeout(fadeOutTimeoutRef.current);
          }
          fadeOutTimeoutRef.current = setTimeout(() => {
            setShowQuestionFlip(false);
            setIsFadingOut(false);
            previousQuestionIdForFlipRef.current = questionId;
          }, FADE_OUT_DURATION);
        } else {
          setShowQuestionFlip(false);
          previousQuestionIdForFlipRef.current = questionId;
        }
      }
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }
    };
  }, [currentQuestion, showQuestionFlip]);

  // Detect when all players have answered to trigger flip
  useEffect(() => {
    if (!currentQuestion || !totalPlayers) return;
    
    // Check if all players have answered
    // If host is playing: need host's answer + all other players' answers
    // If host is not playing: need all players' answers
    const allAnswered = hostIsPlaying 
      ? (answerSubmitted && liveAnswers.length >= totalPlayers - 1)
      : liveAnswers.length >= totalPlayers;
    
    if (allAnswered && currentQuestion.interesting && !showQuestionFlip) {
      // Update the flip ref to track which question we're flipping for
      const currentQuestionId = getQuestionId(currentQuestion);
      previousQuestionIdForFlipRef.current = currentQuestionId;
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setShowQuestionFlip(true);
      }, FLIP_DELAY);
    }
  }, [liveAnswers, totalPlayers, currentQuestion, answerSubmitted, hostIsPlaying, showQuestionFlip]);

  // Trigger flip when timer runs out
  useEffect(() => {
    if (currentQuestion?.interesting && !showQuestionFlip) {
      // This will be triggered externally when timer reaches 0
      // The timer logic will call a function to trigger the flip
    }
  }, [currentQuestion, showQuestionFlip]);

  // Handle question change callback for flip reset
  useEffect(() => {
    if (onQuestionChange && currentQuestion) {
      const questionId = getQuestionId(currentQuestion);
      if (questionId !== previousQuestionIdForFlipRef.current && showQuestionFlip) {
        setIsFadingOut(true);
        if (fadeOutTimeoutRef.current) {
          clearTimeout(fadeOutTimeoutRef.current);
        }
        fadeOutTimeoutRef.current = setTimeout(() => {
          setShowQuestionFlip(false);
          setIsFadingOut(false);
          previousQuestionIdForFlipRef.current = questionId;
        }, FADE_OUT_DURATION);
      }
    }
  }, [currentQuestion, showQuestionFlip, onQuestionChange]);

  return {
    showQuestionFlip,
    setShowQuestionFlip,
    isFadingOut,
    previousQuestionIdForFlipRef,
  };
};

/**
 * Helper function to trigger flip when timer runs out
 * This is used externally when timer reaches 0
 */
export const useTimerFlipTrigger = (
  timeRemaining: number,
  currentQuestion: Question | null,
  showQuestionFlip: boolean,
  setShowQuestionFlip: React.Dispatch<React.SetStateAction<boolean>>,
  previousQuestionIdForFlipRef: React.MutableRefObject<string | undefined>
) => {
  useEffect(() => {
    if (timeRemaining === 0 && currentQuestion?.interesting && !showQuestionFlip) {
      const currentQuestionId = getQuestionId(currentQuestion);
      previousQuestionIdForFlipRef.current = currentQuestionId;
      setTimeout(() => {
        setShowQuestionFlip(true);
      }, FLIP_DELAY);
    }
  }, [timeRemaining, currentQuestion, showQuestionFlip, setShowQuestionFlip, previousQuestionIdForFlipRef]);
};
